import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface InventoryItem {
  id: string
  sku: string
  name: string
  current_stock: number
  reorder_point: number
  optimal_level: number
  days_of_supply: number
  turnover_rate: number
  status: string
}

interface InventoryHealth {
  category: string
  optimal: number
  low: number
  overstock: number
  stockout: number
  value: number
}

interface Statistics {
  total_skus: number
  inventory_value: number
  turnover_rate: number
  stockout_rate: number
  overstock_rate: number
  carrying_cost: number
}

export default function InventoryOptimizationPage() {
  const [items, setItems] = useState<InventoryItem[]>([])
  const [health, setHealth] = useState<InventoryHealth[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [itemsRes, healthRes, statsRes] = await Promise.all([
        fetch('/api/inventoryoptimization/items'),
        fetch('/api/inventoryoptimization/health'),
        fetch('/api/inventoryoptimization/statistics')
      ])
      if (itemsRes.ok) setItems(await itemsRes.json())
      if (healthRes.ok) setHealth(await healthRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching inventory optimization data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const getStatusBadge = (status: string) => {
    if (status === 'optimal') return 'bg-green-100 text-green-800'
    if (status === 'low') return 'bg-yellow-100 text-yellow-800'
    if (status === 'overstock') return 'bg-blue-100 text-blue-800'
    return 'bg-red-100 text-red-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Inventory Optimization</h1>
          <p className="mt-2 text-gray-600">Phase 259 - Stock levels and inventory health</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total SKUs</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(statistics.total_skus)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Inventory Value</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(statistics.inventory_value)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Turnover Rate</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.turnover_rate}x</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Stockout Rate</p>
              <p className="text-2xl font-bold text-red-600">{statistics.stockout_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Overstock Rate</p>
              <p className="text-2xl font-bold text-yellow-600">{statistics.overstock_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Carrying Cost</p>
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(statistics.carrying_cost)}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Inventory Items</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Optimal</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days Supply</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Turnover</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{item.name}</div>
                      <div className="text-xs text-gray-500">{item.sku}</div>
                    </td>
                    <td className="px-6 py-4 text-blue-600 font-medium">{formatNumber(item.current_stock)}</td>
                    <td className="px-6 py-4 text-gray-500">{formatNumber(item.optimal_level)}</td>
                    <td className="px-6 py-4 text-purple-600">{item.days_of_supply}d</td>
                    <td className="px-6 py-4 text-green-600">{item.turnover_rate}x</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(item.status)}`}>{item.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Inventory Health by Category</h2>
          </div>
          <div className="p-6 space-y-4">
            {health.map((cat, idx) => (
              <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{cat.category}</span>
                  <span className="text-lg font-bold text-green-600">{formatCurrency(cat.value)}</span>
                </div>
                <div className="grid grid-cols-5 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Optimal</p>
                    <p className="font-medium text-green-600">{cat.optimal}%</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Low</p>
                    <p className="font-medium text-yellow-600">{cat.low}%</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Overstock</p>
                    <p className="font-medium text-blue-600">{cat.overstock}%</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Stockout</p>
                    <p className="font-medium text-red-600">{cat.stockout}%</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Value</p>
                    <p className="font-medium text-purple-600">{formatCurrency(cat.value)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
